import React, { useEffect, useState } from "react";
import { Row, Col, Form, ListGroup, Button, Badge, Alert, Accordion } from "react-bootstrap";
import { FaPlus, FaTrash } from "react-icons/fa";
import { permissionGroupService } from "../../services/permissionGroup.service";
import { PERMISSIONS, getPermissionsByCategory } from "../../config/permissions";
import { permissionGroupPermissionService } from "../../services/permissionGroupPermission.service";

function GroupPermissionsTab({ user, preSelectedGroup }) {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(preSelectedGroup || null);
  const [groupPermissions, setGroupPermissions] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (preSelectedGroup) {
      setSelectedGroup(preSelectedGroup);
    }
  }, [preSelectedGroup]);

  useEffect(() => {
    if (selectedGroup) {
      loadGroupPermissions(selectedGroup.groupId);
    }
  }, [selectedGroup]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const params = {
        clientId: user.clientId,
        clientGuid: user.clientGuid,
      };

      const groupsResp = await permissionGroupService.getAll(params);
      const groupsData = groupsResp.data || groupsResp;

      setGroups(groupsData);
      setError(null);
    } catch (err) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadGroupPermissions = async (groupId) => {
    if (!groupId) {
      return;
    }

    try {
      const response = await permissionGroupPermissionService.getPermissionsForGroup(groupId);
      const groupPerms = response.data || response;

      setGroupPermissions(groupPerms);

      // Calculate available permissions (not yet assigned to group) - exclude super user only permissions
      const assignedPermissionKeys = groupPerms.map(gp => gp.permissionId);
      const available = PERMISSIONS.filter(p => !assignedPermissionKeys.includes(p.permissionKey) && !p.superUserOnly);
      setAvailablePermissions(available);
    } catch (err) {
      setError("Failed to load group permissions");
    }
  };

  const handleAssignPermission = async (permissionKey) => {
    try {
      await permissionGroupPermissionService.assign({
        groupId: selectedGroup.groupId,
        permissionId: permissionKey,
      });
      loadGroupPermissions(selectedGroup.groupId);
    } catch (err) {
      setError("Failed to assign permission");
    }
  };

  const handleRemovePermission = async (permissionKey) => {
    try {
      await permissionGroupPermissionService.remove(selectedGroup.groupId, permissionKey);
      loadGroupPermissions(selectedGroup.groupId);
    } catch (err) {
      setError("Failed to remove permission");
    }
  };

  const getPermissionDetails = (permissionKey) => {
    return PERMISSIONS.find(p => p.permissionKey === permissionKey);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        {!preSelectedGroup && (
          <Col md={4}>
            <h5>Select Permission Group</h5>
            <ListGroup>
              {groups.filter(g => g.active).map((group) => (
                <ListGroup.Item
                  key={group.groupId}
                  active={selectedGroup?.groupId === group.groupId}
                  onClick={() => setSelectedGroup(group)}
                  style={{ cursor: "pointer" }}
                >
                  {group.groupName}
                  <br />
                  <small className="text-muted">{group.groupDescription}</small>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
        )}

        <Col md={preSelectedGroup ? 12 : 8}>
          {selectedGroup ? (
            <>
              {!preSelectedGroup && (
                <h5>
                  Permissions for: {selectedGroup.groupName}
                  <Badge bg="secondary" className="ms-2">
                    {groupPermissions.length}
                  </Badge>
                </h5>
              )}

              <Row>
                <Col md={6}>
                  <h6>Available Permissions</h6>
                  <div style={{ height: "600px", overflowY: "auto" }}>
                    {availablePermissions.length === 0 ? (
                      <p className="text-muted p-3">All permissions have been assigned</p>
                    ) : (
                      <Accordion defaultActiveKey="0">
                        {Object.entries(
                          availablePermissions.reduce((acc, permission) => {
                            if (!acc[permission.category]) {
                              acc[permission.category] = [];
                            }
                            acc[permission.category].push(permission);
                            return acc;
                          }, {})
                        ).sort(([a], [b]) => a.localeCompare(b)).map(([category, permissions], idx) => (
                          <Accordion.Item eventKey={idx.toString()} key={category}>
                            <Accordion.Header>
                              {category} <Badge bg="secondary" className="ms-2">{permissions.length}</Badge>
                            </Accordion.Header>
                            <Accordion.Body className="p-0">
                              <ListGroup variant="flush">
                                {permissions.map((permission) => (
                                  <ListGroup.Item
                                    key={permission.permissionKey}
                                    className="d-flex justify-content-between align-items-center"
                                  >
                                    <div>
                                      <strong>{permission.permissionName}</strong>
                                      <br />
                                      <small className="text-muted">
                                        {permission.permissionDescription}
                                      </small>
                                    </div>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleAssignPermission(permission.permissionKey)}
                                    >
                                      <FaPlus />
                                    </Button>
                                  </ListGroup.Item>
                                ))}
                              </ListGroup>
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    )}
                  </div>
                </Col>

                <Col md={6}>
                  <h6>Assigned Permissions</h6>
                  <div style={{ height: "600px", overflowY: "auto" }}>
                    {groupPermissions.length === 0 ? (
                      <p className="text-muted p-3">No permissions assigned to this group</p>
                    ) : (
                      <Accordion defaultActiveKey="0">
                        {Object.entries(
                          groupPermissions.reduce((acc, gp) => {
                            const permDetails = getPermissionDetails(gp.permissionId);
                            const category = permDetails?.category || "Other";
                            if (!acc[category]) {
                              acc[category] = [];
                            }
                            acc[category].push({ ...gp, permDetails });
                            return acc;
                          }, {})
                        ).sort(([a], [b]) => a.localeCompare(b)).map(([category, permissions], idx) => (
                          <Accordion.Item eventKey={idx.toString()} key={category}>
                            <Accordion.Header>
                              {category} <Badge bg="secondary" className="ms-2">{permissions.length}</Badge>
                            </Accordion.Header>
                            <Accordion.Body className="p-0">
                              <ListGroup variant="flush">
                                {permissions.map((gp) => (
                                  <ListGroup.Item
                                    key={gp.permissionId}
                                    className="d-flex justify-content-between align-items-center"
                                  >
                                    <div>
                                      <strong>{gp.permDetails?.permissionName || gp.permissionId}</strong>
                                      {gp.permDetails?.superUserOnly && (
                                        <Badge bg="warning" text="dark" className="ms-2">Super User</Badge>
                                      )}
                                      <br />
                                      <small className="text-muted">
                                        {gp.permDetails?.permissionDescription || gp.permissionId}
                                      </small>
                                    </div>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleRemovePermission(gp.permissionId)}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </ListGroup.Item>
                                ))}
                              </ListGroup>
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    )}
                  </div>
                </Col>
              </Row>
            </>
          ) : (
            <p className="text-muted">Select a permission group to manage its permissions</p>
          )}
        </Col>
      </Row>
    </>
  );
}

export default GroupPermissionsTab;
